import React from 'react';
import { Link } from 'react-router-dom';
import { useStudyPlans } from '../../hooks/useStudyPlans';
import { useAuth } from '../../context/AuthContext';
import { getPortalHomePath } from '../../utils/portalPaths';
import { formatUsd } from '../../data/studyPlans';

export default function PricingGrid({ showNote = true, showExtraFees = true }) {
  const { plans, examFee, degreeFee, loading } = useStudyPlans();
  const { currentUser, isAdmin, hasSubscription } = useAuth();

  if (loading && plans.length === 0) {
    return <p className="mk-price-note">Loading plans…</p>;
  }

  function planCta(plan) {
    if (isAdmin) return { to: '/admin', label: 'Admin Console' };
    if (currentUser && hasSubscription) {
      return { to: getPortalHomePath({ isAdmin }), label: 'Dashboard' };
    }
    if (currentUser) {
      return {
        to: '/billing',
        state: { planId: plan.id },
        label: `Enroll — ${plan.name.replace(/ GED Subscription$/i, '').replace(/ Study Plan$/i, '')}`,
      };
    }
    return {
      to: `/checkout?plan=${encodeURIComponent(plan.id)}`,
      label: `Enroll — ${plan.name.replace(/ GED Subscription$/i, '').replace(/ Study Plan$/i, '')}`,
    };
  }

  return (
    <>
      <div className="mk-pricing-grid">
        {plans.map((plan) => {
          const cta = planCta(plan);
          return (
            <article
              key={plan.id}
              className={`mk-price-card${plan.featured ? ' is-featured' : ''}`}
            >
              {plan.badge && <span className="mk-price-badge">{plan.badge}</span>}
              <h3 className="mk-price-name">{plan.name}</h3>
              <p className="mk-price-duration">{plan.durationLabel}</p>
              <div className="mk-price-row">
                <span className="mk-price-now">{formatUsd(plan.price)}</span>
                {plan.discountPercent > 0 && (
                  <span className="mk-price-was">{formatUsd(plan.regularPrice)}</span>
                )}
              </div>
              {plan.discountPercent > 0 && (
                <p className="mk-price-save">
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                    sell
                  </span>
                  {plan.discountPercent}% off — save {formatUsd(plan.regularPrice - plan.price)}
                </p>
              )}
              <p className="mk-price-desc">{plan.description}</p>
              <ul className="mk-price-features">
                {(plan.features || []).map((f) => (
                  <li key={f}>
                    <span className="material-symbols-outlined">check_circle</span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={cta.to}
                state={cta.state}
                className={`mk-btn mk-btn-block ${plan.featured ? 'mk-btn-gold' : 'mk-btn-primary'}`}
              >
                {cta.label}
              </Link>
            </article>
          );
        })}
      </div>

      {showExtraFees ? (
        <div className="mk-extra-fees">
          <div className="mk-extra-fees-head">
            <span className="mk-eyebrow">Additional fees</span>
            <h3>Exam fee &amp; degree charges</h3>
            <p>Amounts set by the academy. Service charges and taxes are not included below.</p>
          </div>
          <div className="mk-extra-fees-grid">
            <div className="mk-extra-fee-item">
              <span className="material-symbols-outlined">quiz</span>
              <div>
                <strong>GED Exam fee</strong>
                <p>Per subject exam fee</p>
              </div>
              <span className="mk-extra-fee-amount">{formatUsd(examFee.perSubjectUsd)}</span>
            </div>
            <div className="mk-extra-fee-item">
              <span className="material-symbols-outlined">workspace_premium</span>
              <div>
                <strong>Degree charges</strong>
                <p>Diploma &amp; transcript</p>
              </div>
              <span className="mk-extra-fee-amount">{formatUsd(degreeFee.diplomaTranscriptUsd)}</span>
            </div>
          </div>
        </div>
      ) : null}

      {showNote && (
        <p className="mk-price-note">
          Discounted admission fee shown above. After enrollment, choose your plan and complete payment to unlock
          the full portal.
        </p>
      )}
    </>
  );
}
